import * as React from "react";
import { cn } from "../../lib/utils";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  noWFull?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", noWFull, ...props }, ref) => {
    const width = noWFull ? "" : "w-full";
    return (
      <input
        ref={ref}
        type={type}
        className={cn(
          "h-9 " +
            width +
            " rounded-card border border-line bg-surface-card px-3 text-sm text-ink placeholder:text-ink-muted focus:border-teal focus:outline-none disabled:opacity-50",
          className,
        )}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export interface NumberInputProps
  extends Omit<InputProps, "type" | "onChange" | "value"> {
  value: number | undefined;
  onValueChange: (value: number | undefined) => void;
  unit?: string;
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value, onValueChange, unit, ...props }, ref) => {
    const [text, setText] = React.useState<string>(
      value == null ? "" : formatNumber(value),
    );
    const [isFocused, setIsFocused] = React.useState(false);
    const [justFocused, setJustFocused] = React.useState(false);

    React.useEffect(() => {
      if (!isFocused) {
        setText(value == null ? "" : formatNumber(value));
      }
    }, [value, isFocused]);

    const commit = (raw: string) => {
      const trimmed = raw.trim().replace(",", ".");
      if (trimmed === "") {
        onValueChange(undefined);
        return;
      }
      const parsed = Number(trimmed);
      if (!Number.isNaN(parsed)) onValueChange(parsed);
    };

    return (
      <div className="relative">
        <Input
          ref={ref}
          {...props}
          type="number"
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => {
            commit(e.target.value);
            setIsFocused(false);
            setJustFocused(false);
            props.onBlur?.(e);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            setJustFocused(true);
            const target = e.target;
            setTimeout(() => {
              target.select();
            }, 50);
            props.onFocus?.(e);
          }}
          onMouseUp={(e) => {
            if (justFocused) {
              e.preventDefault();
              e.currentTarget.select();
              setJustFocused(false);
            }
          }}
          className={cn(unit && "pr-10", className)}
        />
        {unit && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-ink-muted">
            {unit}
          </span>
        )}
      </div>
    );
  },
);
NumberInput.displayName = "NumberInput";

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 10) / 10);
}
